import { MultiselectProps, SelectProps } from '@amzn/awsui-components-react-v3';

import { DocumentEntry, QuoteEntry, QuoteTemplateGenerationStatus } from './';
import { CoManagerEntry } from './index';

export interface SupplierEntry {
  supplierId: string;
  supplierName: string;
  contactList: string;
  contactSource: string;
  status: string;
  countryCode?: string;
  incoterm?: string;
  nullIncotermReason?: string;
  qualificationStatus?: string;
  vendorServiceCurrencies?: string[];
  selectedCurrencyValid?: boolean;
}

/**
 * Life cycle graphs for RFXRequest entity
 * Backend ref: https://code.amazon.com/packages/AWSSCMRFXService/blobs/mainline/--/src/com/aws/scm/rfxservice/domain/view/dao/RFXRequestLifeCycleGraphDao.java
 */
export enum RFXRequestState {
  DRAFT = 'Draft',
  ASSIGNED = 'Assigned',
  VENDORS_PICKED = 'Vendors_Picked',
  PUBLISHED = 'Published',
  REVIEW_BIDS = 'Review_Bids',
  PENDING_SELECTION = 'Pending_Selection',
  SOFT_AWARDED = 'Soft_Awarded',
  COMPLETED = 'Completed',
  ARCHIVED = 'Archived',
  DISCARDED = 'Discarded',
  ROUND_TRANSITIONED = 'Round_Transitioned',
  RECALLED = 'Recalled'
}

export function convertStringToRFXRequestState(stateString: string): RFXRequestState {
  return RFXRequestState[stateString.toUpperCase() as keyof typeof RFXRequestState];
}

export interface RFXRequestBaseInformation {
  userRfxId: string;
  fullRfxId: string;
  parentRfxId?: string;
  parentRfxRound?: number;
  rfxType: string;
  rfxName: string;
  region: SelectProps.Option;
  regions?: MultiselectProps.Options;
  siteNameFBN?: string | null;
  businessId?: string | null;
  workflow?: string | null;
  beginDate: string;
  beginTime: string;
  dueDate: string;
  dueTime: string;
  needByDate: string;
  needByTime: string;
  organization: SelectProps.Option;
  businessUnit: SelectProps.Option;
  department: SelectProps.Option;
  defaultQuoteValidity?: number;
  project: string;
  currency: SelectProps.Option;
  quoteTemplateType?: SelectProps.Option;
  rfxLineItemClassId: string;
  overview: string;
  processDetails: string;
  simUrl?: string;
}

interface RFXRequestSingleInformation extends RFXRequestBaseInformation {
  category: SelectProps.Option;
  subCategory?: SelectProps.Option;
  categories?: never;
}

export interface RFXRequestMultipleInformation extends RFXRequestBaseInformation {
  category?: never;
  categories: MultiselectProps.Options;
  subCategory?: SelectProps.Option;
}

export const DCSPEventSubTypes = {
  Competitive: 'Competitive',
  Repricing: 'Repricing',
  SSJ: 'SSJ',
  ContractRenewal: 'Contract renewal'
};

export type YesNoNull = 'Yes' | 'No' | null;

export type YesNo = 'Yes' | 'No';

/**
 * Interface for DCSP event sub type details when the selection is "Repricing" or "Contract renewal"
 *
 * isSourceEventIdAvailable: whether original sourcing event ID is available
 * sourceEventId: source event reference if isSourceEventIdAvailable is 'Yes'
 * comments: comments if isSourceEventIdAvailable is 'No'
 */
export interface ReworkProps {
  isSourceEventIdAvailable: YesNo;
  sourceEventId: string;
  comments: string;
}

/**
 * DCSP Event sub type
 *
 * option:   event sub type, can be one of "Competitive", "Repricing", "SSJ", or "Contract renewal"
 * isAligned: the answer to "Bids award criteria have been aligned with the customer?", when
 *            "Competitive" is selected
 * uploadedFile: file name of the supporting document if "isAligned" is "Yes"
 * ssjLink:   mandatory SSJ link when "SSJ" is selected
 * repricing: details of repricing
 * renewal:   details of contract renewal
 */
export interface EventSubTypeProps {
  option: SelectProps.Option | null;
  isAligned?: YesNo;
  nonAlignmentReason?: string;
  uploadedFile?: string;
  ssjLink?: string;
  repricing?: ReworkProps;
  renewal?: ReworkProps;
}

export interface DCSPRFXRequestInformation extends RFXRequestBaseInformation {
  category: SelectProps.Option;
  categories?: never;
  subCategory?: SelectProps.Option;
  estimatedAmount?: string;
  sites?: MultiselectProps.Options;
  eventSubType: EventSubTypeProps;
  isBuildIdAvailable?: YesNo;
  buildId?: string;
  buildIdComments?: string;
}

export type RFXRequestGeneralInformation =
  | RFXRequestSingleInformation
  | RFXRequestMultipleInformation
  | DCSPRFXRequestInformation;

export interface CommodityInformation {
  rootId: string;
  commodityIds: string[];
  category: string | undefined;
}

export interface RFXRequestQuotes {
  quoteEntries: QuoteEntry[];
  commodityInformation: CommodityInformation | undefined;
  quoteTemplateGenerationStatus: QuoteTemplateGenerationStatus | undefined;
}

export interface RFXRequestSuppliers {
  supplierEntries: SupplierEntry[];
  existingSupplierContactsMap: { [supplier: string]: string };
}

export interface RFXRequestCoManagers {
  coManagerEntries: CoManagerEntry[];
}

export interface RFXRequestDocuments {
  documentEntries: DocumentEntry[];
  supportDocuments?: DocumentEntry[];
}

export interface RFXRequestReview {
  // No data
}

export interface TrackingInformation {
  publishedAt?: number | undefined;
  awardedAt?: number | undefined;
}

export interface RFXRequestForm {
  state: RFXRequestState;
  round: number;
  rfxRequestVersion: number;
  generalInformation: RFXRequestGeneralInformation;
  quotes: RFXRequestQuotes;
  suppliers: RFXRequestSuppliers;
  'co-managers': RFXRequestCoManagers;
  documents: RFXRequestDocuments;
  review: RFXRequestReview;
  trackingInformation: TrackingInformation;
}

export type RequestFormKey = 'generalInformation' | 'quotes' | 'suppliers' | 'co-managers' | 'documents' | 'review';

export type RequestOnChangeParam<K extends RequestFormKey> = Partial<Pick<RFXRequestForm, K>[K]>;

export type RequestOnChangeFn<K extends RequestFormKey> = (data: RequestOnChangeParam<K>) => void;

export enum RFXRequestAction {
  CREATE_REQUEST,
  EDIT_ROUND,
  INITIATE_ROUND,
  CLONE_REQUEST
}
